import { useState } from 'react';
import {
  ActivityIndicator,
  Image,
  Modal,
  Pressable,
  ScrollView,
  Text,
  View,
} from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { MessageSquarePlus, RefreshCw, Send, X } from 'lucide-react-native';
import html2canvas from 'html2canvas';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { useToast } from '@/hooks/useToast';
import { useAuth } from '@/hooks/useAuth';
import { apiRequestJson } from '@/lib/queryClient';
import { useTRPCClient } from '@/lib/trpc';
import { colors, typography } from '@/lib/theme';

const feedbackBackdropColor = 'rgba(22, 36, 28, 0.58)';

type WebFeedbackWidgetProps = {
  compact?: boolean;
  variant?: 'floating' | 'header' | 'masthead';
};

type UploadTargetResponse = {
  uploadURL: string;
  objectPath?: string;
};

type ViewportInfo = {
  width: number;
  height: number;
  pixelRatio: number;
};

type CaptureResult = {
  blob: Blob;
  previewUrl: string;
};

async function capturePageScreenshot(): Promise<CaptureResult> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    throw new Error('Screenshot capture is only available on web.');
  }

  const captureTarget = document.getElementById('root') ?? document.body;
  const canvas = await html2canvas(captureTarget, {
    backgroundColor: colors.ink.paper,
    logging: false,
    scale: Math.min(window.devicePixelRatio || 1, 2),
    useCORS: true,
    windowWidth: window.innerWidth,
    windowHeight: window.innerHeight,
    scrollX: window.scrollX,
    scrollY: window.scrollY,
  });

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((nextBlob) => {
      if (!nextBlob) {
        reject(new Error('Failed to encode screenshot.'));
        return;
      }
      resolve(nextBlob);
    }, 'image/png');
  });

  return {
    blob,
    previewUrl: canvas.toDataURL('image/png'),
  };
}

function getCurrentPageLabel() {
  if (typeof window === 'undefined' || typeof window.location === 'undefined' || window.location == null) {
    return null;
  }

  const pathname = typeof window.location.pathname === 'string' ? window.location.pathname : '';
  const search = typeof window.location.search === 'string' ? window.location.search : '';
  const hash = typeof window.location.hash === 'string' ? window.location.hash : '';

  return `${pathname}${search}${hash}`;
}

export default function WebFeedbackWidget({ compact = false, variant = 'floating' }: WebFeedbackWidgetProps) {
  const { toast } = useToast();
  const { isAuthenticated } = useAuth();
  const trpcClient = useTRPCClient();
  const [isVisible, setIsVisible] = useState(false);
  const [message, setMessage] = useState('');
  const [screenshotBlob, setScreenshotBlob] = useState<Blob | null>(null);
  const [screenshotPreviewUrl, setScreenshotPreviewUrl] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const currentPageLabel = getCurrentPageLabel();

  if (!isAuthenticated) {
    return null;
  }

  const resetForm = () => {
    setMessage('');
    setScreenshotBlob(null);
    setScreenshotPreviewUrl(null);
    setIsCapturing(false);
    setIsSubmitting(false);
  };

  const handleOpen = async () => {
    resetForm();
    setIsCapturing(true);

    try {
      const result = await capturePageScreenshot();
      setScreenshotBlob(result.blob);
      setScreenshotPreviewUrl(result.previewUrl);
    } catch (error: any) {
      toast.show({
        type: 'error',
        title: 'Screenshot Failed',
        description: error?.message || 'Could not capture the current page.',
      });
    } finally {
      setIsCapturing(false);
      setIsVisible(true);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    resetForm();
  };

  const handleRefreshScreenshot = async () => {
    setScreenshotBlob(null);
    setScreenshotPreviewUrl(null);
    setIsCapturing(true);
    try {
      const result = await capturePageScreenshot();
      setScreenshotBlob(result.blob);
      setScreenshotPreviewUrl(result.previewUrl);
    } catch (error: any) {
      toast.show({
        type: 'error',
        title: 'Screenshot Failed',
        description: error?.message || 'Could not capture the current page.',
      });
    } finally {
      setIsCapturing(false);
    }
  };

  const handleSubmit = async () => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage) {
      toast.show({
        type: 'error',
        title: 'Message Required',
        description: 'Add a few details before sending feedback.',
      });
      return;
    }

    if (!screenshotBlob) {
      toast.show({
        type: 'error',
        title: 'Screenshot Required',
        description: 'Refresh the screenshot and try again.',
      });
      return;
    }

    const viewport: ViewportInfo | null =
      typeof window !== 'undefined'
        ? {
            width: window.innerWidth,
            height: window.innerHeight,
            pixelRatio: window.devicePixelRatio || 1,
          }
        : null;
    const page = getCurrentPageLabel();

    setIsSubmitting(true);

    try {
      const { uploadURL, objectPath: providedObjectPath } = await apiRequestJson<UploadTargetResponse>(
        '/api/objects/upload',
        'POST',
      );

      const uploadResponse = await fetch(uploadURL, {
        method: 'PUT',
        headers: {
          'Content-Type': 'image/png',
        },
        body: screenshotBlob,
      });

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        throw new Error(errorText || `Upload failed with status ${uploadResponse.status}`);
      }

      const screenshotObjectPath = providedObjectPath || new URL(uploadURL).pathname;

      await trpcClient.feedback.create.mutate({
        category: 'other',
        message: trimmedMessage,
        page,
        screenshotObjectPath,
        userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : null,
        viewport,
      });

      toast.show({
        type: 'success',
        title: 'Feedback Sent',
        description: 'Your note and screenshot were submitted.',
      });
      handleClose();
    } catch (error: any) {
      toast.show({
        type: 'error',
        title: 'Submit Failed',
        description: error?.message || 'Could not submit feedback.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Pressable
        onPress={() => void handleOpen()}
        style={[
          variant === 'masthead'
            ? styles.mastheadButton
            : variant === 'header'
              ? styles.headerButton
              : styles.floatingButton,
          variant === 'masthead' && compact && styles.mastheadButtonCompact,
        ]}
        disabled={isCapturing}
        accessibilityRole="button"
        accessibilityLabel="Open feedback widget"
      >
        <MessageSquarePlus
          size={variant === 'masthead' ? (compact ? 22 : 28) : 18}
          color={variant === 'masthead' ? colors.text.inverse : colors.text.primary}
        />
        {variant === 'header' || variant === 'masthead' ? null : (
          <Text style={styles.floatingButtonText}>{isCapturing ? 'Capturing...' : 'Feedback'}</Text>
        )}
      </Pressable>

      <Modal visible={isVisible} transparent animationType="fade" onRequestClose={handleClose}>
        <View style={styles.overlay}>
          <Pressable style={styles.backdrop} onPress={handleClose} />
          <View style={styles.panel}>
            <View style={styles.headerRow}>
              <View style={styles.headerTextWrap}>
                <Text style={styles.title}>Send Feedback</Text>
                <Text style={styles.subtitle}>Tell us what happened and include the current page.</Text>
              </View>
              <Pressable onPress={handleClose} style={styles.closeButton}>
                <X size={18} color={colors.text.secondary} />
              </Pressable>
            </View>

            <ScrollView contentContainerStyle={styles.content}>
              <View style={styles.section}>
                <Text style={styles.label}>Message</Text>
                <Textarea
                  value={message}
                  onChangeText={setMessage}
                  rows={6}
                  placeholder="Describe the issue, question, or idea."
                />
              </View>

              <View style={styles.section}>
                <View style={styles.sectionHeaderRow}>
                  <Text style={styles.label}>Screenshot</Text>
                  <Button
                    variant="outline"
                    size="sm"
                    onPress={handleRefreshScreenshot}
                    disabled={isCapturing || isSubmitting}
                  >
                    <RefreshCw size={14} color={colors.text.secondary} />
                    <Text style={styles.inlineButtonText}>
                      {isCapturing ? 'Capturing...' : 'Refresh'}
                    </Text>
                  </Button>
                </View>

                <View style={styles.previewFrame}>
                  {isCapturing ? (
                    <View style={styles.previewPlaceholder}>
                      <ActivityIndicator size="small" color={colors.text.primary} />
                      <Text style={styles.previewPlaceholderText}>Capturing current page…</Text>
                    </View>
                  ) : screenshotPreviewUrl ? (
                    <Image source={{ uri: screenshotPreviewUrl }} style={styles.previewImage} resizeMode="contain" />
                  ) : (
                    <View style={styles.previewPlaceholder}>
                      <Text style={styles.previewPlaceholderText}>No screenshot available.</Text>
                    </View>
                  )}
                </View>
              </View>

              <View style={styles.section}>
                <Text style={styles.label}>Page</Text>
                <Text style={styles.pageLabel}>{currentPageLabel || 'Unknown page'}</Text>
              </View>
            </ScrollView>

            <View style={styles.footerRow}>
              <Button
                variant="outline"
                onPress={handleClose}
                disabled={isSubmitting}
              >
                <Text style={styles.secondaryButtonText}>Cancel</Text>
              </Button>
              <Button onPress={() => void handleSubmit()} disabled={isSubmitting || isCapturing}>
                <Send size={16} color={colors.text.primary} />
                <Text style={styles.primaryButtonText}>{isSubmitting ? 'Sending...' : 'Send Feedback'}</Text>
              </Button>
            </View>
          </View>
        </View>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create(() => ({
  backdrop: {
    backgroundColor: feedbackBackdropColor,
    bottom: 0,
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  closeButton: {
    alignItems: 'center',
    borderColor: colors.ink.press,
    borderRadius: 0,
    borderWidth: 2,
    height: 32,
    justifyContent: 'center',
    width: 32,
  },
  content: {
    gap: 16,
    paddingBottom: 8,
  },
  floatingButton: {
    alignItems: 'center',
    backgroundColor: colors.ink.acid,
    borderColor: colors.ink.press,
    borderRadius: 0,
    borderWidth: 2,
    bottom: 24,
    elevation: 4,
    flexDirection: 'row',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    position: 'absolute',
    right: 24,
    shadowColor: colors.ink.press,
    shadowOffset: { width: 8, height: 8 },
    shadowOpacity: 1,
    shadowRadius: 0,
    zIndex: 999,
  },
  floatingButtonText: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 14,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  footerRow: {
    flexDirection: 'row',
    gap: 12,
    justifyContent: 'flex-end',
    marginTop: 16,
  },
  headerButton: {
    alignItems: 'center',
    backgroundColor: colors.ink.acid,
    borderColor: colors.ink.press,
    borderRadius: 0,
    borderWidth: 2,
    height: 40,
    justifyContent: 'center',
    width: 40,
  },
  headerRow: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  headerTextWrap: {
    flex: 1,
    gap: 4,
    paddingRight: 12,
  },
  inlineButtonText: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 13,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  label: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.8,
    marginBottom: 8,
    textTransform: 'uppercase',
  },
  mastheadButton: {
    alignItems: 'center',
    backgroundColor: colors.ink.fieldDeep,
    borderRadius: 999,
    height: 56,
    justifyContent: 'center',
    width: 56,
  },
  mastheadButtonCompact: {
    height: 40,
    width: 40,
  },
  overlay: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    padding: 24,
  },
  pageLabel: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 12,
  },
  panel: {
    backgroundColor: colors.card,
    borderColor: colors.ink.press,
    borderRadius: 0,
    borderWidth: 3,
    elevation: 6,
    maxHeight: '90%',
    maxWidth: 720,
    padding: 24,
    shadowColor: colors.ink.press,
    shadowOffset: { width: 14, height: 14 },
    shadowOpacity: 1,
    shadowRadius: 0,
    width: '100%',
  },
  previewFrame: {
    alignItems: 'center',
    backgroundColor: colors.ink.paperDark,
    borderColor: colors.ink.press,
    borderRadius: 0,
    borderWidth: 2,
    justifyContent: 'center',
    minHeight: 240,
    overflow: 'hidden',
    padding: 12,
  },
  previewImage: {
    height: 320,
    maxWidth: '100%',
    width: '100%',
  },
  previewPlaceholder: {
    alignItems: 'center',
    gap: 8,
    justifyContent: 'center',
    paddingVertical: 32,
  },
  previewPlaceholderText: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 13,
    textTransform: 'uppercase',
  },
  primaryButtonText: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 14,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  secondaryButtonText: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 14,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  section: {
    gap: 4,
  },
  sectionHeaderRow: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  subtitle: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.body,
    fontSize: 14,
    lineHeight: 20,
  },
  title: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.display,
    fontSize: 26,
    fontWeight: '900',
    textTransform: 'uppercase',
  },
}));
